import {Component, computed, signal, ChangeDetectionStrategy} from "@angular/core"
import {isInstanceOf} from "@cm/utils"
import {quaternionToAngleDegrees} from "@app/common/helpers/utils/math-utils"
import {TemplateTreeObjectTransformComponent} from "@app/template-editor/components/template-tree-object-transform/template-tree-object-transform.component"
import {
    RigidRelation,
    BallRotation,
    FixedRotation,
    HingeRotation,
    Translation,
    ObjectLikeNode,
    R1Variable,
    S1Variable,
    S3Variable,
    ObjectNode,
} from "@cm/template-nodes"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {z} from "zod/v4-mini"
import {
    resolveSelectionPossibilityValue,
    SelectionPossibilities,
    SelectionPossibility,
    ValueSlotComponent,
} from "@app/template-editor/components/value-slot/value-slot.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-rigid-relation-inspector",
    templateUrl: "./rigid-relation-inspector.component.html",
    styleUrl: "./rigid-relation-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, TemplateTreeObjectTransformComponent, ButtonComponent],
})
export class RigidRelationInspectorComponent extends BaseInspectorComponent<RigidRelation> {
    readonly $translation = computed(() => this.$parameters().translation)
    readonly $rotation = computed(() => this.$parameters().rotation)
    translationTypePossibilites: SelectionPossibilities<R1Variable | number> = [
        {value: 0, name: "Fixed"},
        {value: () => new R1Variable({parameters: {default: 0, range: [-100, 100]}}), name: "Variable"},
    ]
    translationTypeSelected = (selectionPossibility: SelectionPossibility<R1Variable | number>, value: R1Variable | number) => {
        const selectionPossibilityValue = resolveSelectionPossibilityValue(selectionPossibility.value)
        return typeof value === typeof selectionPossibilityValue
    }
    isR1Variable = isInstanceOf(R1Variable)
    readonly $numberSchema = signal(z.number())
    rotationTypePossibilites: SelectionPossibilities<FixedRotation | HingeRotation | BallRotation> = [
        {value: () => new FixedRotation({parameters: {x: 0, y: 0, z: 0}}), name: "Fixed"},
        {
            value: () =>
                new HingeRotation({
                    parameters: {
                        axis: "x",
                        rotation: new S1Variable({
                            parameters: {
                                default: [0, 1],
                            },
                        }),
                    },
                }),
            name: "Hinge",
        },
        {
            value: () =>
                new BallRotation({
                    parameters: {
                        rotation: new S3Variable({
                            parameters: {
                                default: [0, 0, 0, 1],
                            },
                        }),
                    },
                }),
            name: "Ball",
        },
    ]
    rotationTypeSelected = (
        selectionPossibility: SelectionPossibility<FixedRotation | HingeRotation | BallRotation>,
        value: FixedRotation | HingeRotation | BallRotation,
    ) => {
        const selectionPossibilityValue = resolveSelectionPossibilityValue(selectionPossibility.value)
        return value.nodeClass === selectionPossibilityValue.nodeClass
    }
    isFixedRotation = isInstanceOf(FixedRotation)
    isHingeRotation = isInstanceOf(HingeRotation)
    isBallRotation = isInstanceOf(BallRotation)
    readonly $hingeRotationAxisPossibilites = signal<SelectionPossibilities<"x" | "y" | "z">>([
        {value: "x", name: "X"},
        {value: "y", name: "Y"},
        {value: "z", name: "Z"},
    ])
    readonly $isObject = signal(isInstanceOf(ObjectNode))
    private getMatrix(target: ObjectLikeNode | null) {
        if (!target) return undefined
        if (!(target instanceof ObjectNode)) return undefined
        return this.sceneManagerService.getTransformAccessor(target)?.getTransform()
    }
    readonly $matrixA = computed(() => this.getMatrix(this.$node().parameters.targetA))
    readonly $matrixB = computed(() => this.getMatrix(this.$node().parameters.targetB))

    captureRelationParams() {
        const matrixA = this.$matrixA()
        const matrixB = this.$matrixB()

        if (!matrixA) return
        if (!matrixB) return

        const {position, quaternion} = matrixA.inverse().multiply(matrixB).decompose()
        const angles = quaternionToAngleDegrees(quaternion)

        const node = this.$node()
        this.sceneManagerService.modifyTemplateGraph(() => {
            node.updateParameters({
                translation: new Translation({parameters: {x: position.x, y: position.y, z: position.z}}),
                rotation: new FixedRotation({parameters: {x: angles[0], y: angles[1], z: angles[2]}}),
            })
        })
    }
}
